import { cn } from '@/lib/utils';
import { BRAND } from '@/lib/brand';

type Tone = 'humus' | 'white' | 'bright-moss';

const TONE_FILL: Record<Tone, string> = {
  humus: BRAND.humus,
  white: '#FFFFFF',
  'bright-moss': BRAND.brightMoss,
};

interface BrandMarkProps {
  /**
   * Approved colorways only (Brand Guidelines p.10):
   *   - 'humus'       Humus on light backgrounds (default)
   *   - 'white'       White on Humus / dark backgrounds
   *   - 'bright-moss' Bright Moss on Humus (alternate, accent moments only)
   */
  tone?: Tone;
  className?: string;
  title?: string;
}

/**
 * Inline-rendered Virro Knot (the brand symbol). Geometry is approved
 * proportionally — never scale x/y independently. Per Brand Guidelines p.9,
 * the Knot represents two interlocking forms suggesting separation and
 * reconnection simultaneously.
 *
 * For the production rasterized version (favicon, OG image, PWA icons), see
 * scripts/generate-brand-icons.mjs.
 */
export function BrandMark({ tone = 'humus', className, title = 'Virro' }: BrandMarkProps) {
  const fill = TONE_FILL[tone];
  return (
    <svg
      viewBox="0 0 64 64"
      xmlns="http://www.w3.org/2000/svg"
      role="img"
      aria-label={title}
      className={cn('shrink-0', className)}
    >
      <title>{title}</title>
      {/* The Virro Knot: two interlocking rings — closed-loop separation+reconnection. */}
      <g fill="none" stroke={fill} strokeWidth={5.5} strokeLinecap="round">
        <circle cx="22" cy="32" r="14" />
        <circle cx="42" cy="32" r="14" />
      </g>
    </svg>
  );
}
